import { BareActionContext, getStoreBuilder } from 'vuex-typex';
import { RootState } from '@/store/state';
import { AuthenticationState } from './state';
import Oidc, { UserManager, User, WebStorageStateStore } from 'oidc-client';
import appStore from '@/store/app';
import { Environment, UserProfile } from '@/types';
import { Result } from '@/models';

type StoreContext = BareActionContext<AuthenticationState, RootState>;

const thisState: AuthenticationState = {
    contextUser: undefined,
};

const userManager = new UserManager({
    authority: process.env.VUE_APP_IDENTITY_AUTHORITY,
    client_id: process.env.VUE_APP_IDENTITY_CLIENTID,
    redirect_uri: `${process.env.VUE_APP_URL}/auth-callback`,
    post_logout_redirect_uri: process.env.VUE_APP_URL,
    response_type: process.env.VUE_APP_IDENTITY_RESPONSETYPE,
    scope: process.env.VUE_APP_IDENTITY_SCOPE,
    silent_redirect_uri: `${process.env.VUE_APP_URL}/silent-refresh`,
    accessTokenExpiringNotificationTime: parseInt(process.env.VUE_APP_IDENTITY_ACCESSTOKENEXPIRINGNOTIFICATIONTIME, 0),
    automaticSilentRenew: false,
    filterProtocolClaims: true,
    loadUserInfo: true,
    userStore: new WebStorageStateStore({ prefix: process.env.VUE_APP_IDENTITY_CLIENTID, store: window.localStorage }),
});

if (process.env.NODE_ENV === Environment.Development) {
    Oidc.Log.logger = console;
    Oidc.Log.level = Oidc.Log.WARN;
}

const moduleBuilder = getStoreBuilder<RootState>().module('authentication', thisState);

const thisModule = {
    getters: {
        contextUser(state: AuthenticationState): User | undefined {
            return state.contextUser || undefined;
        },

        userProfile(state: AuthenticationState): UserProfile {
            if (state.contextUser && state.contextUser.profile) {
                const userProfile = {...state.contextUser.profile, ...{} as UserProfile } as UserProfile;
                // const userProfile = state.contextUser.profile as UserProfile;
                if (userProfile.role && !Array.isArray(userProfile.role)) {
                    userProfile.role = [ userProfile.role ];
                }
                return userProfile;
            } else {
                return {} as UserProfile;
            }
        },

        accessToken(state: AuthenticationState): string | undefined {
            return state.contextUser ? state.contextUser.access_token : undefined;
        },

        isLoggedIn(state: AuthenticationState): boolean {
            return !!state.contextUser && !state.contextUser.expired && !!state.contextUser.access_token;
        },
    },

    actions: {

        async checkSignInAsync(context: StoreContext): Promise<void> {
            const user = await authentication.getUserAsync();
            if (!user || user.expired) {
                return authentication.startSignInAsync();
            }
            return;
        },

        async startSignInAsync(context: StoreContext): Promise<void> {

            appStore.loading({ isLoading: true });
            commitSignOutUser();

            return await userManager.signinRedirect()
                .catch((reason) => {
                    if (reason) {
                        appStore.error({ message: reason.message || 'Errors' });
                    }
                })
                .finally(() => {
                    appStore.loading({ isLoading: false });
                });
        },

        async completeSignInAsync(context: StoreContext): Promise<Result> {

            appStore.loading({ isLoading: false });

            return await userManager.signinRedirectCallback()
                .then((user) => {
                    commitAddAuthenticatedUser(user);
                    return new Result(true);
                })
                .catch((reason) => {
                    commitSignOutUser();
                    appStore.error({ message: reason.message });
                    return new Result(false, { error: reason.error, message: reason.message });
                })
                .finally(() => {
                    appStore.loading({ isLoading: false });
                });
        },

        async signOutAsync(context: StoreContext): Promise<void> {
            return await userManager.signoutRedirect()
                .catch((reason) => {
                    appStore.error({ message: reason.message });
                })
                .finally(() => {
                    commitSignOutUser();
                });
        },

        async signinSilentAsync(context: StoreContext): Promise<void> {
            return await userManager.signinSilent()
                .then((user) => {
                    commitAddAuthenticatedUser(user);
                })
                .catch((reason) => {
                    appStore.error({ message: reason.message, timeout: 3000 });
                });
        },

        async signinSilentCallbackAsync(context: StoreContext): Promise<void | User | undefined> {
            return await userManager.signinSilentCallback()
                // .then((result) => { })
                .catch((reason) => {
                    appStore.error({ message: reason.message });
                });
        },

        async getUserAsync(context: StoreContext): Promise<User | undefined> {
            const user = await userManager.getUser();
            if (user && !user.expired) {
                commitAddAuthenticatedUser(user);
                return user;
            } else {
                commitSignOutUser();
                return undefined;
            }
        },
    },

    mutations: {
        addAuthenticatedUser(state: AuthenticationState, payload: User) {
            state.contextUser = payload;
        },

        signOut(state: AuthenticationState) {
            state.contextUser = undefined;
        },
    },
};

// private getters
const getLoggedInUser = moduleBuilder.read(thisModule.getters.contextUser, 'contextUser');
const getUserProfile = moduleBuilder.read(thisModule.getters.userProfile, 'userProfile');
const getIsLoggedIn = moduleBuilder.read(thisModule.getters.isLoggedIn, 'isLoggedIn');
const getAccessToken = moduleBuilder.read(thisModule.getters.accessToken, 'accessToken');

// private mutations
const commitAddAuthenticatedUser = moduleBuilder.commit(thisModule.mutations.addAuthenticatedUser, 'addAuthenticatedUser');
const commitSignOutUser = moduleBuilder.commit(thisModule.mutations.signOut, 'signOut');

const authentication = {

    // getters (wrapped as real getters)
    get contextUser() { return getLoggedInUser(); },
    get userProfile() { return getUserProfile(); },
    get accessToken() { return getAccessToken(); },
    get isLoggedIn() { return getIsLoggedIn(); },

    // actions
    checkSignInAsync: moduleBuilder.dispatch(thisModule.actions.checkSignInAsync, 'checkSignInAsync'),
    startSignInAsync: moduleBuilder.dispatch(thisModule.actions.startSignInAsync, 'startSignInAsync'),
    completeSignInAsync: moduleBuilder.dispatch(thisModule.actions.completeSignInAsync, 'completeSignInAsync'),
    signOutAsync: moduleBuilder.dispatch(thisModule.actions.signOutAsync, 'signOutAsync'),
    getUserAsync: moduleBuilder.dispatch(thisModule.actions.getUserAsync, 'getUserAsync'),
    signinSilentAsync: moduleBuilder.dispatch(thisModule.actions.signinSilentAsync, 'signinSilentAsync'),
    signinSilentCallbackAsync: moduleBuilder.dispatch(thisModule.actions.signinSilentCallbackAsync, 'signinSilentCallbackAsync'),
};

userManager.events.addUserLoaded((user) => {
    if (user) {
        appStore.info({ message: `Utente ${user.profile.given_name} ${user.profile.family_name} autenticato.`, timeout: 1000 });
    }
});

userManager.events.addAccessTokenExpiring(async () => {
    appStore.info({ message: 'Token di accesso in scadenza.', timeout: 2000 });
    await authentication.signinSilentAsync();
});

userManager.events.addAccessTokenExpired(() => {
    appStore.info({ message: 'Token di accesso scaduto.', timeout: 2000 });
    commitSignOutUser();
});

userManager.events.addSilentRenewError((reason) => {
    appStore.error({ message: reason.message, timeout: 6000 });
});

export default authentication;
